// frontend/src/pages/SkillChart.js - Supabase対応版（Part 1: Imports & fetchData）
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Radar, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,      // ✅ 追加
  LinearScale,        // ✅ 追加
  BarElement,         // ✅ 追加
} from "chart.js";
import { supabase } from "../supabase";
import { getSkillData, saveSkillRecords } from "../supabase";
import Header from "../components/ui/Header";
import SavingOverlay from "../components/ui/SavingOverlay";
import { useOrganization } from "../contexts/OrganizationContext"; // 追加
// 既存のインポートの後に追加
import ProcedureDetailModal from "../components/ProcedureDetailModal";
import BulkSkillInputModal from "../components/BulkSkillInputModal";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,      // ✅ 追加
  LinearScale,        // ✅ 追加
  BarElement         // ✅ 追加
);

const statusOptions = [
  "未経験", "見学済み", "経験1回", "経験2回",
  "経験3回以上", "外から見守り", "独り立ち", "指導可",
];

const statusMap = Object.fromEntries(statusOptions.map((v, i) => [v, i]));
const statusColors = {
  "未経験": "#e0e0e0",
  "見学済み": "#ffe0b2",
  "経験1回": "#ffcc80",
  "経験2回": "#ffb74d",
  "経験3回以上": "#ffa726",
  "外から見守り": "#66bb6a",
  "独り立ち": "#26a69a",
  "指導可": "#00897b"
};

function ModernSkillChart() {
  const { staffId } = useParams();
  const { currentOrganization, currentUser } = useOrganization();
  const [staffName, setStaffName] = useState("");
  const [departments, setDepartments] = useState([]);
  const [proceduresByDept, setProceduresByDept] = useState({});
  const [combatPower, setCombatPower] = useState(0);
  const [staffYear, setStaffYear] = useState(null);
  const [titles, setTitles] = useState([]);
  const [shiftPatterns, setShiftPatterns] = useState([]);
  const [activeDept, setActiveDept] = useState("all");
  const [loading, setLoading] = useState(true);
  const [savingChanges, setSavingChanges] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [autoSaving, setAutoSaving] = useState(false);  // ✅ 追加
  const [lastSaved, setLastSaved] = useState(null);      // ✅ 追加
  const [checklistProgress, setChecklistProgress] = useState({});
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [checklistMasters, setChecklistMasters] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false); // 🔥 一括入力モーダル

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  const recalculateCombatPower = (dataMap) => {
    let total = 0;
    let count = 0;
    
    Object.values(dataMap).forEach(proc => {
      if (proc.requiredRoles && proc.requiredRoles.length > 0) {
        proc.requiredRoles.forEach(role => {
          const roleKey = role.label ? `${role.type}_${role.label}` : role.type;
          const skillLevel = proc.skills[roleKey];
          if (skillLevel && skillLevel in statusMap) {
            total += statusMap[skillLevel];
            count++;
          }
        });
      } else {
        if (proc.scrub && proc.scrub in statusMap) {
          total += statusMap[proc.scrub];
          count++;
        }
        if (proc.circulating && proc.circulating in statusMap) {
          total += statusMap[proc.circulating];
          count++;
        }
      }
    });
    
    const score = count > 0 ? Math.round((total / (count * 7)) * 100) : 0;
    setCombatPower(score);
  };

  const fetchData = async () => {
    if (!currentOrganization?.id) return; // 追加
    setLoading(true);
    try {
      // Edge Function経由でデータ取得
      const data = await getSkillData(staffId, currentOrganization.id);

      // ✅ チェックリストマスター取得（追加）
      const { data: masterData } = await supabase
      .from("procedure_checklists")
      .select("*")
      .eq("organization_id", currentOrganization.id);

      const { data: progressData } = await supabase
      .from("checklist_progress")
      .select("*")
      .eq("user_id", staffId)
      .eq("organization_id", currentOrganization.id);

      const progressMap = {};
      (progressData || []).forEach(item => {
        const key = `${item.procedure_id}_${item.role}`;
        progressMap[key] = item.completed_items || [];
      });
      setChecklistProgress(progressMap);                                

      const masterMap = {};
      (masterData || []).forEach(item => {
      const key = `${item.procedure_id}_${item.role}`;
      const totalItems = (item.checklist_items || []).reduce((sum, cat) => sum + cat.items.length, 0);
      masterMap[key] = totalItems;
      });
      setChecklistMasters(masterMap);
      
      const deptList = data.departments || [];
      const procList = (data.procedures || []).map(proc => ({
        ...proc,
        required_roles: typeof proc.required_roles === 'string' 
          ? JSON.parse(proc.required_roles) 
          : (proc.required_roles || [])
      }));
      const skillList = data.skillRecords || [];
      const staff = data.staff;

      if (staff) {
        setStaffName(`${staff.last_name} ${staff.first_name}`);
        setStaffYear(Number(staff.year));
      }

      // スキルマップ構築
      const skillMap = {};
      skillList.forEach(skill => {
        const key = skill.label ? 
          `${skill.procedure_id}_${skill.role}_${skill.label}` :
          `${skill.procedure_id}_${skill.role}`;
        skillMap[key] = skill;
      });

      // 診療科ごとにグループ化
      const grouped = {};
      deptList.forEach(dept => {
        const procs = procList.filter(p => p.department_id === dept.id);
        grouped[dept.name] = procs.map(p => {
          const proc = {
            id: p.id,
            name: p.name,
            departmentName: dept.name,
            requiredRoles: p.required_roles || [],
            skills: {},
            skillIds: {},
            scrub: "未経験",
            circulating: "未経験",
            scrubId: null,
            circulatingId: null,
          };

          if (p.required_roles && p.required_roles.length > 0) {
            p.required_roles.forEach(role => {
              const key = role.label ? 
                `${p.id}_${role.type}_${role.label}` :
                `${p.id}_${role.type}`;
              
              const roleKey = role.label ? `${role.type}_${role.label}` : role.type;
              proc.skills[roleKey] = skillMap[key]?.level || "未経験";
              proc.skillIds[roleKey] = skillMap[key]?.id;
            });
          } else {
            const scrubKey = `${p.id}_scrub`;
            const circKey = `${p.id}_circulating`;
            
            proc.scrub = skillMap[scrubKey]?.level || "未経験";
            proc.circulating = skillMap[circKey]?.level || "未経験";
            proc.scrubId = skillMap[scrubKey]?.id;
            proc.circulatingId = skillMap[circKey]?.id;
          }

          return proc;
        });
      });

      setDepartments(deptList);
      setProceduresByDept(grouped);
      recalculateCombatPower(flattenProcedures(grouped));
      
      // 👇 デバッグログ（ここに移動）
      console.log('📊 Debug Info:', {
        departmentCount: deptList.length,
        procedureCount: procList.length,
        skillRecordCount: skillList.length,
        sampleProcedure: procList[0],
        sampleSkillRecord: skillList[0],
        skillMapKeys: Object.keys(skillMap).slice(0, 5)
      });
      
    } catch (error) {
      console.error("データの取得に失敗:", error);
      showNotification("データの取得に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ 診療科で使用されている役割タイプを取得する関数
const getRoleTypesForDept = (dept) => {
  const procs = proceduresByDept[dept];
  if (!procs || procs.length === 0) return [];
  
  const roleTypes = new Set();
  
  procs.forEach(p => {
    if (p.requiredRoles && p.requiredRoles.length > 0) {
      // required_rolesが定義されている場合
      p.requiredRoles.forEach(role => {
        if (role.type === 'scrub' || role.type === 'circulating') {
          roleTypes.add(role.type);
        }
      });
    } else {
      // required_rolesが定義されていない場合はデフォルトで両方
      roleTypes.add('scrub');
      roleTypes.add('circulating');
    }
  });
  
  return Array.from(roleTypes);
};

// ✅ 全診療科で使用されている役割タイプを取得する関数
const getAllRoleTypes = () => {
  const roleTypes = new Set();
  
  Object.keys(proceduresByDept).forEach(dept => {
    const deptRoles = getRoleTypesForDept(dept);
    deptRoles.forEach(role => roleTypes.add(role));
  });
  
  return Array.from(roleTypes);
};

  const flattenProcedures = (data) =>
    Object.values(data).flat().reduce((acc, p) => {
      acc[p.id] = p;
      return acc;
    }, {});

    useEffect(() => {
      if (currentOrganization?.id) {  // ← 組織IDがある場合のみ
        fetchData();
      }
    }, [staffId, currentOrganization?.id]);  // ← 依存配列に追加
  
    useEffect(() => {
      // shift_patternsはまだデータがないので空配列
      setTitles([]);
    }, [proceduresByDept, combatPower, staffId, shiftPatterns, staffYear]);
  
    const handleChange = (dept, procId, roleType, roleLabel, newLevel) => {
      // ローカルステートを更新
      setProceduresByDept(prev => {
        const updated = { ...prev };
        updated[dept] = updated[dept].map(p => {
          if (p.id === procId) {
            if (p.requiredRoles && p.requiredRoles.length > 0) {
              const roleKey = roleLabel ? `${roleType}_${roleLabel}` : roleType;
              return {
                ...p,
                skills: {
                  ...p.skills,
                  [roleKey]: newLevel
                }
              };
            } else {
              return { ...p, [roleType]: newLevel };
            }
          }
          return p;
        });
        recalculateCombatPower(flattenProcedures(updated));
        return updated;
      });
      
      // ✅ 自動保存を実行
      autoSaveChange(dept, procId, roleType, roleLabel, newLevel);
    };

    // カードクリック時のハンドラー
const handleProcedureClick = (proc, dept) => {
  setSelectedProcedure({
    ...proc,
    departmentName: dept
  });
  setModalOpen(true);
};

const handleModalClose = () => {
  setModalOpen(false);
  setSelectedProcedure(null);
  // ✅ モーダルを閉じる時にデータを再読み込み
  fetchData();
};

const handleModalSave = () => {
  // データ再読み込み
  fetchData();
};


    const handleBulkUpdate = (levelLabel) => {
      setProceduresByDept(prev => {
        const updated = { ...prev };
        for (const dept in updated) {
          updated[dept] = updated[dept].map(p => {
            if (p.requiredRoles && p.requiredRoles.length > 0) {
              const newSkills = {};
              p.requiredRoles.forEach(role => {
                const roleKey = role.label ? `${role.type}_${role.label}` : role.type;
                newSkills[roleKey] = levelLabel;
              });
              return {
                ...p,
                skills: newSkills
              };
            } else {
              return {
                ...p,
                scrub: levelLabel,
                circulating: levelLabel,
              };
            }
          });
        }
        recalculateCombatPower(flattenProcedures(updated));
        return updated;
      });
    };
  
    

    // ✅ 自動保存関数（個別の変更を即座に保存）
const autoSaveChange = async (dept, procId, roleType, roleLabel, newLevel) => {
  if (!currentOrganization?.id) return;
  
  setAutoSaving(true);
  try {
    // 変更された1つの項目だけを保存
    const record = {
      user_id: String(staffId),
      procedure_id: procId,
      role: roleType,
      level: newLevel,
      organization_id: currentOrganization.id
    };
    
    if (roleLabel) {
      record.label = roleLabel;
    }
    
    // 1件だけ保存
    await saveSkillRecords([record], currentOrganization.id);
    
    setLastSaved(new Date());
    console.log("✅ 自動保存完了:", record);
  } catch (error) {
    console.error("❌ 自動保存エラー:", error);
    showNotification("保存に失敗しました", "error");
  } finally {
    setAutoSaving(false);
  }
};
  
const getChecklistProgressPercent = (procedureId, role) => {
  const progressKey = `${procedureId}_${role}`;
  const masterKey = `${procedureId}_${role}`;
  
  const completedItems = checklistProgress[progressKey] || [];
  const totalItems = checklistMasters[masterKey] || 0;
  
  if (totalItems === 0) return 0;
  return Math.round((completedItems.length / totalItems) * 100);
};  
  
    const createRadarData = (roleType) => {
      const labels = [];
      const values = [];
    
      for (const dept in proceduresByDept) {
        const procs = proceduresByDept[dept];
        const nums = [];
        
        procs.forEach(p => {
          if (p.requiredRoles && p.requiredRoles.length > 0) {
            p.requiredRoles.forEach(role => {
              if (role.type === roleType) {
                const roleKey = role.label ? `${role.type}_${role.label}` : role.type;
                const level = statusMap[p.skills[roleKey]] ?? 0;
                nums.push(level);
              }
            });
          } else {
            if (roleType === "scrub" || roleType === "circulating") {
              const level = statusMap[p[roleType]] ?? 0;
              nums.push(level);
            }
          }
        });
        
        const avg = nums.length ? Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 100 / 7) : 0;
        labels.push(dept);
        values.push(avg === 0 ? 5 : avg);
      }
    
      // ✅ 項目数に応じてチャートタイプを決定
      const chartType = labels.length <= 2 ? 'bar' : 'radar';

      return {
        type: chartType,
        labels,
        datasets: [
          {
            label: `${roleType === "scrub" ? "器械出し" : "外回り"} 全診療科`,
            data: values,
            backgroundColor: roleType === "scrub" ? "rgba(38, 166, 154, 0.2)" : "rgba(233, 30, 99, 0.2)",
            borderColor: roleType === "scrub" ? "#26a69a" : "#e91e63",
            borderWidth: 2,
          },
        ],
      };
    };                      
  
    const createDeptRadarData = (dept, roleType) => {
      const procs = proceduresByDept[dept];
      const labels = [];
      const data = [];
      
      procs.forEach(p => {
        if (p.requiredRoles && p.requiredRoles.length > 0) {
          p.requiredRoles.forEach(role => {
            if (role.type === roleType) {
              const label = role.label ? `${p.name}(${role.label})` : p.name;
              const roleKey = role.label ? `${role.type}_${role.label}` : role.type;
              labels.push(label);
              const level = statusMap[p.skills[roleKey]] ?? 0;
              data.push(level === 0 ? 0.5 : level);
            }
          });
        } else {
          if (roleType === "scrub" || roleType === "circulating") {
            labels.push(p.name);
            const level = statusMap[p[roleType]] ?? 0;
            data.push(level === 0 ? 0.5 : level);
          }
        }
      });
      
      // ✅ 項目数に応じてチャートタイプを決定
      const chartType = labels.length <= 2 ? 'bar' : 'radar';

      return {
        type: chartType,
        labels,
        datasets: [
          {
            label: roleType === "scrub" ? "器械出し" : "外回り",
            data,
            backgroundColor: roleType === "scrub" ? "rgba(38, 166, 154, 0.2)" : "rgba(233, 30, 99, 0.2)",
            borderColor: roleType === "scrub" ? "#26a69a" : "#e91e63",
            borderWidth: 2,
          },
          ...(chartType === 'radar' ? [{
            label: "独り立ちライン",
            data: labels.map(() => 6),
            backgroundColor: "rgba(0, 0, 0, 0)",
            borderColor: "rgba(0, 200, 83, 1)",
            borderWidth: 2,
            borderDash: [5, 5],
            pointRadius: 0,
          }] : []),
        ],
      };
    };

  // 組織が選択されていない場合
  if (!currentOrganization?.id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-700">組織を選択してください</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-700">データを読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-8">
      <SavingOverlay visible={autoSaving} message="経験録を保存中..." />
      {notification.show && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          notification.type === "error" ? "bg-red-500" : "bg-teal-500"
        } text-white`}>
          {notification.message}
        </div>
      )}

      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Staff Profile Card */}
        {/* Staff Profile Card */}
<div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
  {/* ... 既存のコンテンツ ... */}
</div>

{/* ✅ 自動保存インジケーター */}
{(autoSaving || lastSaved) && (
  <div className="bg-white rounded-lg shadow-md p-3 mb-6 flex items-center justify-between">
    {autoSaving ? (
      <div className="flex items-center gap-2 text-blue-600">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm font-medium">保存中...</span>
      </div>
    ) : lastSaved ? (
      <div className="flex items-center gap-2 text-green-600">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
        </svg>
        <span className="text-sm font-medium">
          保存完了 ({new Date(lastSaved).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })})
        </span>
      </div>
    ) : null}
  </div>
)}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
          <div className="md:flex">
            <div className="md:w-1/3 bg-gradient-to-r from-teal-500 to-teal-600 p-6 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl font-bold text-white mb-2">{combatPower}</div>
                <div className="text-teal-100">達成度</div>
              </div>
            </div>
            <div className="p-6 md:w-2/3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="uppercase tracking-wide text-sm text-teal-500 font-semibold">経験録</div>
                  <h2 className="text-3xl font-bold text-gray-900 mt-1 mb-4">{staffName}</h2>
                </div>
                {/* 🔥 一括入力ボタン: 初期設定などで全術式まとめて入力したいニーズ用 */}
                <button
                  onClick={() => setBulkOpen(true)}
                  className="px-4 py-2 rounded-lg text-sm font-bold text-white shadow-md hover:shadow-lg transition-all"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #5b21b6)' }}
                >
                  📝 一括入力
                </button>
              </div>
              
              {titles.length > 0 && (
                <div className="mt-3">
                  <h3 className="text-sm font-medium text-gray-500">資格・称号</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {titles.map((title, i) => (
                      <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                        {title}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Overall Skill Charts */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">全体スキル概要</h2>
          <div className="grid md:grid-cols-3 gap-6">
  {getAllRoleTypes().map(roleType => (
    <div key={roleType} className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-center text-lg font-medium text-gray-700 mb-4">
        {roleType === "scrub" ? "器械出し" : "外回り"} スキル
      </h3>
      <div style={{ height: '300px' }}>
              {(() => {
          const chartData = createRadarData(roleType);
          
          if (chartData.type === 'bar') {
            return (
              <Bar 
                data={chartData}
                options={{
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      ticks: { 
                        stepSize: 20,
                        callback: function(value) {
                          // レベル指標を表示
                          const levels = {
                            0: '0: 未経験',
                            20: '1-2: 見学・経験',
                            40: '3-4: 経験複数回',
                            60: '5: 外から見守り',
                            80: '6: 独り立ち',
                            100: '7: 指導可'
                          };
                          return levels[value] || value;
                        }
                      }
                    }
                  },
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { position: 'bottom' },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return context.dataset.label + ': ' + context.parsed.y + '%';
                        }
                      }
                    }
                  }
                }} 
              />
            );
          } else {
            return (
              <Radar 
                data={chartData}
                options={{
                  scales: {
                    r: {
                      angleLines: { display: true },
                      suggestedMin: 0,
                      suggestedMax: 100,
                      ticks: {
                        stepSize: 20,
                        callback: function(value) {
                          // レベル指標を簡易表示
                          const levels = {
                            0: '0',
                            20: '1-2',
                            40: '3-4',
                            60: '5',
                            80: '6',
                            100: '7'
                          };
                          return levels[value] || value;
                        }
                      }
                    }
                  },
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { position: 'bottom' },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const level = Math.round(context.parsed.r / (100/7));
                          const statusText = statusOptions[level] || '未設定';
                          return context.dataset.label + ': ' + statusText;
                        }
                      }
                    }
                  }
                }} 
              />
            );
          }
        })()}
      </div>
    </div>
  ))}
</div>
        </div>

        {/* Quick Action Buttons */}
<div className="bg-white rounded-xl shadow-md p-6 mb-6">
  <h2 className="text-xl font-bold text-gray-800 mb-4">一括操作</h2>
  <div className="flex flex-wrap gap-4 justify-center">
    <button
      onClick={() => handleBulkUpdate("独り立ち")}
      className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium transition duration-150 ease-in-out"
    >
      全てを独り立ちに設定
    </button>
    <button
      onClick={() => handleBulkUpdate("指導可")}
      className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-3 rounded-lg font-medium transition duration-150 ease-in-out"
    >
      全てを指導可に設定
    </button>
  </div>
</div>

        {/* Department Navigation */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">診療科切り替え</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveDept("all")}
              className={`px-4 py-2 rounded-lg transition duration-150 ease-in-out ${
                activeDept === "all" ? "bg-teal-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"
              }`}
            >
              全体表示
            </button>
            {Object.keys(proceduresByDept).map((dept) => (
              <button
                key={dept}
                onClick={() => setActiveDept(dept)}
                className={`px-4 py-2 rounded-lg transition duration-150 ease-in-out ${
                  activeDept === dept ? "bg-teal-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                }`}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>

        {/* Department Details */}
{Object.entries(proceduresByDept)
  .filter(([dept]) => activeDept === "all" || dept === activeDept)
  .map(([dept, procs], idx) => (
    <div key={idx} className="bg-white rounded-xl shadow-md p-6 mb-6">
      <div className="flex flex-wrap justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">{dept}</h2>
      </div>

              {/* Department Radar Charts */}
              {/* Department Radar Charts */}
<div className="grid md:grid-cols-3 gap-6 mb-6">
  {getRoleTypesForDept(dept).map(roleType => (
    <div key={roleType} className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-center text-lg font-medium text-gray-700 mb-4">
        {roleType === "scrub" ? "器械出し" : "外回り"} スキル（{dept}）
      </h3>
      <div style={{ height: '300px' }}>
              {(() => {
          const chartData = createDeptRadarData(dept, roleType);
          
          if (chartData.type === 'bar') {
            return (
              <Bar 
                data={chartData}
                options={{
                  indexAxis: 'y',
                  scales: {
                    x: {
                      beginAtZero: true,
                      max: 7,
                      ticks: { 
                        stepSize: 1,
                        callback: function(value) {
                          // レベル指標を表示
                          return statusOptions[value] || value;
                        }
                      }
                    }
                  },
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { position: 'bottom' },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const level = context.parsed.x;
                          const statusText = statusOptions[Math.floor(level)] || '未設定';
                          return context.dataset.label + ': ' + statusText;
                        }
                      }
                    }
                  }
                }} 
              />
            );
          } else {
            return (
              <Radar 
                data={chartData}
                options={{
                  scales: {
                    r: {
                      angleLines: { display: true },
                      suggestedMin: 0,
                      suggestedMax: 7,
                      ticks: { 
                        stepSize: 1,
                        callback: function(value) {
                          // レベル指標を表示
                          return statusOptions[value] || value;
                        }
                      }
                    }
                  },
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { position: 'bottom' },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const level = Math.round(context.parsed.r);
                          const statusText = statusOptions[level] || '未設定';
                          return context.dataset.label + ': ' + statusText;
                        }
                      }
                    }
                  }
                }} 
              />
            );
          }
        })()}
      </div>
    </div>
  ))}
</div>

              

              {/* Procedures List - カード形式 */}
<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
  {procs.map((p) => {
    const roles = p.requiredRoles && p.requiredRoles.length > 0
      ? p.requiredRoles
      : [
          { type: "scrub", label: null },
          { type: "circulating", label: null }
        ];
    
    return (
      <div 
        key={p.id} 
        className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-200"
      >
        {/* ヘッダー：術式名 + 詳細ボタン */}
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-bold text-gray-800 text-lg flex-1 mr-2">
            {p.name}
          </h3>
          <button 
            onClick={() => handleProcedureClick(p, dept)}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex-shrink-0"
          >
            詳細
          </button>
        </div>

        {/* 役割別の情報 */}
        <div className="space-y-3">
          {roles.map((role, idx) => {
            const roleKey = role.label ? `${role.type}_${role.label}` : role.type;
            const level = p.skills[roleKey] || p[role.type] || "未経験";
            const roleLabel = role.label || (role.type === "scrub" ? "器械出し" : "外回り");
            const progressPercent = getChecklistProgressPercent(p.id, role.type);

            return (
              <div 
                key={idx} 
                className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50"
              >
                {/* 役割名 */}
                <div className="text-sm font-bold text-gray-700 mb-2">
                  {roleLabel}
                </div>

                {/* 他者評価 */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">他者評価</span>
                  <span
                    className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{
                      backgroundColor: statusColors[level],
                      color: ["未経験", "見学済み", "経験1回", "経験2回"].includes(level) ? "black" : "white"
                    }}
                  >
                    {level}
                  </span>
                </div>

                {/* チェックリスト進捗 */}
                {progressPercent > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">チェックリスト</span>
                    <span className="text-teal-600 font-medium">{progressPercent}%</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  })}
</div>
            </div>
          ))}
      </main>
      {/* モーダル */}
      <ProcedureDetailModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        procedure={selectedProcedure}
        staffId={staffId}
        organizationId={currentOrganization?.id}
        currentUser={currentUser}
        onSave={handleModalSave}
      />

      {/* 🔥 一括入力モーダル */}
      <BulkSkillInputModal
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        staffId={staffId}
        organizationId={currentOrganization?.id}
        proceduresByDept={proceduresByDept}
        onSaved={() => {
          // 保存後、 データを再取得して chart を更新
          window.location.reload();
        }}
      />
    </div>
  );
}

export default ModernSkillChart;